/**
 * Install origin — was this Tracker installed on a clean device, or did it come from an earlier one?
 *
 * Why: the periscope install guide (src/periscopeTutorial.js) must only ever greet a FRESH install. A
 * tablet that already ran Tracker and merely updated must never see it, and no other signal can tell
 * the two apart reliably: `bronze_last_login` does not exist before v4.48 and is per device id (a
 * re-paired tablet looks new). What does tell them apart is the one moment in a page's life when
 * localStorage is still empty: the very first thing the app does. A browser profile / WebView that
 * has never run Tracker has no key at all; any tablet that has run any version has at least one
 * (the Cognito tokens, `device_id`, ...). The answer is written once, under `bronze:installOrigin`,
 * and never recomputed.
 *
 * This file imports nothing on purpose: src/bootInstallOrigin.js calls it as the first module of the
 * bundle, and a dependency evaluated before it could write to localStorage first and turn a fresh
 * install into a "legacy" one.
 */

/** localStorage key that holds the answer. */
export const INSTALL_ORIGIN_KEY = 'bronze:installOrigin';

/** `fresh`: first run on a clean device. `legacy`: anything else, including "could not tell". */
export const INSTALL_ORIGIN = Object.freeze({ FRESH: 'fresh', LEGACY: 'legacy' });

const ORIGINS = Object.values(INSTALL_ORIGIN);

/** The stored origin: 'fresh' | 'legacy', or null when absent, unknown or unreadable. */
export function readInstallOrigin(storage) {
  if (!storage) return null;
  try {
    const value = storage.getItem(INSTALL_ORIGIN_KEY);
    return ORIGINS.includes(value) ? value : null;
  } catch (_) {
    return null;
  }
}

/**
 * Record the origin the first time it is called on this storage and return it; afterwards return
 * what was recorded. An empty storage is a fresh install, any key at all is a legacy one. When the
 * storage cannot be read or written the answer is 'legacy' (and nothing is written): the cost of a
 * wrong 'legacy' is a tutorial not offered, the cost of a wrong 'fresh' is a kitchen interrupted.
 * Never throws.
 */
export function markInstallOrigin(storage) {
  if (!storage) return INSTALL_ORIGIN.LEGACY;
  try {
    const existing = storage.getItem(INSTALL_ORIGIN_KEY);
    if (ORIGINS.includes(existing)) return existing;
    // An unrecognised value is a key: the storage is not empty, so it can only be legacy.
    const origin = existing === null && storage.length === 0 ? INSTALL_ORIGIN.FRESH : INSTALL_ORIGIN.LEGACY;
    storage.setItem(INSTALL_ORIGIN_KEY, origin);
    return origin;
  } catch (_) {
    return INSTALL_ORIGIN.LEGACY;
  }
}
